import React, { memo, useEffect, useRef, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import AgGridComponent from '../../sharedcomponents/ag-grid/AgGrid';
import ModalBox from './../../sharedcomponents/modal-box/ModalBox';
import smartHttp from '../../helpers/smartHttp';
import { Endpoints, GenericError, UserId } from '../../constants';
import { Notify } from './../../sharedcomponents/Alert/Notify';
import store from '../../redux/store';
import { debounce } from '../../helpers/debounce';

// NOTE: This component is common for Add Entitlement & Add User Group
const DistributorUserIdModal = memo(
  ({
    filteredDistributorUserIdData,
    handleEntitlementSave,
    handleClose,
    isUserGroupComp = false,
  }) => {
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errordetails, setErrordetails] = useState('');
    const [isTradeEntityHide, setIsTradeEntityHide] = useState(true);

    const ManageUserIdDistributors =
      store.getState()?.dbConfig.ManageUserIdDistributors;
    const TradingEntityDistributorPlatforms = ManageUserIdDistributors?.filter(
      (item) => item.tradingEntity === 1,
    );
    const DistributorUserIDRequiredList = store
      .getState()
      ?.dbConfig.ManageUserIdDistributors?.filter(
        (item) => item.isRequired === 1,
      );

    useEffect(() => {
      const reduceCallback = setTimeout(() => {
        setRowData(filteredDistributorUserIdData);
      }, 1000);
      return () => clearTimeout(reduceCallback);
    }, [filteredDistributorUserIdData]);

    const onInputsChanged = (params) => {
      let index = rowData.findIndex(
        (x) =>
          x.distributorId === params.data?.distributorId &&
          x.contactId === params.data?.contactId,
      );
      if (index >= 0) {
        rowData[index]['userId'] = params.data.userId ?? '';
        rowData[index]['contact_Xref_Number'] =
          params.data.contact_Xref_Number ?? '';
      }

      // Check if all mandatory fields are now filled
      if (errordetails) {
        const allValid = rowData.every((du) => {
          // Check required user id
          if (
            DistributorUserIDRequiredList.some(
              (e) => e.distributorId === du.distributorId,
            )
          ) {
            if (!du?.userId?.trim()) return false;
          }
          // Check required trading entity
          if (
            TradingEntityDistributorPlatforms.some(
              (e) => e.distributorId === du.distributorId,
            )
          ) {
            if (!du?.contact_Xref_Number?.trim()) return false;
          }
          return true;
        });

        // Only clear error if all mandatory fields are filled
        if (allValid) {
          setErrordetails('');
        }
      }

      setRowData([...rowData]);
    };

    const defaultConfig = {
      rowModelType: 'clientSide',
      isExportCSV: false,
      isExportExcel: false,
      pivotPanelShow: '',
      pagination: false,
      isAutoSizeColums: true,
      enableCharts: false,
      sideBar: false,
      rowGroupPanelShow: false,
    };

    const gridRef = useRef();
    const defaultColdef = {
      sortable: true,
      resizable: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      enableValue: false,
      enableRowGroup: false,
    };

    const columns = [
      {
        headerName: 'User',
        field: 'contactData',
        tooltipField: 'contactData',
        initialFlex: 2,
      },
      {
        headerName: 'Delivery Platform',
        field: 'distributorData',
        tooltipField: 'distributorData',
        initialFlex: 4,
        cellRenderer: (params) => {
          if (
            params?.data &&
            (DistributorUserIDRequiredList.some(
              (e) => e.distributorId === params.data.distributorId,
            ) ||
              TradingEntityDistributorPlatforms.some(
                (e) => e.distributorId === params.data.distributorId,
              ))
          ) {
            return (
              <div style={{ position: 'relative' }}>
                <span
                  className="asterik"
                  style={{
                    position: 'absolute',
                    left: '-12px',
                  }}
                >
                  *
                </span>
                <span>{params.value}</span>
              </div>
            );
          } else return params.value;
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        tooltipField: 'status',
        initialFlex: 2,
        hide: isUserGroupComp,
      },
      {
        headerName: 'User Id',
        headerTooltip: 'User Id',
        field: 'userId',
        tooltipField: 'userId',
        initialFlex: 3,
        singleClickEdit: true,
        editable: true,
        cellRenderer: (params) => {
          return (
            <div className="aggrid-input-border">
              {params.data?.userId ? params.data.userId : ''}
            </div>
          );
        },
        menuTabs: ['generalMenuTab'],
        cellClass: (params) => {
          const isRequired = DistributorUserIDRequiredList.some(
            (e) => e.distributorId === params.data?.distributorId,
          );
          const isEmpty = !params.data?.userId || !params.data.userId.trim();
          const showError = isRequired && isEmpty && errordetails;
          return showError
            ? 'aggrid-input-wrapper cell-error-highlight'
            : 'aggrid-input-wrapper';
        },
      },
      {
        headerName: 'Trading Entity Name',
        headerTooltip: 'Trading Entity Name',
        field: 'contact_Xref_Number',
        tooltipField: 'contact_Xref_Number',
        initialFlex: 4,
        hide: isTradeEntityHide,
        singleClickEdit: true,
        editable: (params) => {
          return (
            params?.data &&
            TradingEntityDistributorPlatforms.some(
              (x) => x.distributorId === params.data.distributorId,
            )
          );
        },
        cellRenderer: (params) => {
          const isTradingEntity =
            params?.data &&
            TradingEntityDistributorPlatforms.some(
              (x) => x.distributorId === params.data.distributorId,
            );
          return (
            <div
              className={`aggrid-input-border ${
                !isTradingEntity ? 'aggrid-disabled-cell' : ''
              }`}
            >
              {isTradingEntity ? params.data.contact_Xref_Number : 'N/A'}
            </div>
          );
        },
        menuTabs: ['generalMenuTab'],
        cellClass: (params) => {
          const isTradingEntity =
            params?.data &&
            TradingEntityDistributorPlatforms.some(
              (x) => x.distributorId === params.data.distributorId,
            );
          const isEmpty =
            !params.data?.contact_Xref_Number ||
            !params.data.contact_Xref_Number.trim();
          const showError = isTradingEntity && isEmpty && errordetails;
          return showError
            ? 'aggrid-input-wrapper cell-error-highlight'
            : 'aggrid-input-wrapper';
        },
      },
      {
        headerName: 'User ID Format',
        headerTooltip: 'User ID Format',
        field: 'userIdFormat',
        tooltipField: 'userIdFormat',
        initialFlex: 2,
      },
    ];

    const onSourceUserGridReadyHandler = (params) => {
      gridRef.current = params;
      params.api.showLoadingOverlay();
    };

    const saveDistributor = async (postUserDistributor) => {
      try {
        setLoading(true);
        let postDistributorUserResponse = await smartHttp.post(
          Endpoints.distributorUserApi,
          postUserDistributor,
        );
        return postDistributorUserResponse;
      } catch (err) {
        Notify({
          alert: true,
          type: 'error',
          title: GenericError.somethingWentWrong,
        });
      } finally {
        setLoading(false);
      }
    };
    useEffect(() => {
      if (gridRef.current && gridRef.current.api && rowData.length > 0) {
        const shouldHide = rowData.every(
          (row) =>
            !TradingEntityDistributorPlatforms.some(
              (x) => x.distributorId === row.distributorId,
            ),
        );
        setIsTradeEntityHide(shouldHide);
        // Hide or show the column
      }
    }, [rowData]);

    const handleUserIdSave = async () => {
      if (
        rowData.filter(
          (u) =>
            (DistributorUserIDRequiredList.some(
              (e) => e.distributorId === u.distributorId,
            ) &&
              !u.userId?.trim()) ||
            (TradingEntityDistributorPlatforms.some(
              (e) => e.distributorId === u.distributorId,
            ) &&
              !u.contact_Xref_Number?.trim()),
        ).length === 0
      ) {
        setErrordetails('');
        let postUserDistributor = {
          distributorUsers: rowData.map((ur) => {
            return {
              userId: ur.userId,
              contactId: ur.contactId,
              distributorId: ur.distributorId,
              contact_xref_number: ur.contact_Xref_Number,
            };
          }),
          userId: UserId,
        };
        let postDistributorUserResponse = await saveDistributor(
          postUserDistributor,
        );
        if (postDistributorUserResponse) {
          handleEntitlementSave();
        }
      } else {
        setErrordetails(
          'Please ensure all fields indicated as mandatory are specified',
        );
      }
    };

    const textLength = 9999;
    const unSelectInputText = (params, colId) => {
      if (params && colId && params.column.getColId() === colId) {
        if (params.column && params.rowIndex != null) {
          const cellElement = document.activeElement;
          if (cellElement && typeof cellElement.blur === 'function') {
            // Call the function to unselect all text
            cellElement.setSelectionRange(textLength, textLength);
          }
        }
      }
    };

    return (
      <>
        <ModalBox
          backdrop="static"
          size="xl"
          showLoader={loading}
          show={filteredDistributorUserIdData?.length > 0}
          handleClose={handleClose}
          showHeader={true}
          modalTitle={
            <>
              <span className="edit-user-subtitle">
                {`Please enter User Id for below user${
                  filteredDistributorUserIdData?.length > 1 ? 's' : ''
                }`}
              </span>
            </>
          }
        >
          <div className="container">
            <div className="row">
              <div
                className={`col ag-grid-table ${
                  isUserGroupComp && 'user-group'
                }`}
              >
                <AgGridComponent
                  config={defaultConfig}
                  defaultColumnDef={defaultColdef}
                  data={rowData}
                  suppressRowClickSelection={false}
                  columns={columns}
                  onCellValueChanged={onInputsChanged}
                  onCellEditingStarted={(e) => unSelectInputText(e, 'userId')}
                  onGridReady={onSourceUserGridReadyHandler}
                />
              </div>
            </div>
            <Row className="modal-row">
              <div className="col-8">
                <span className="d-block email-preference-note-label">
                  <span className="text-danger">*</span> Mandatory
                </span>
                <span className="text-danger">{errordetails}</span>
              </div>
              <div className="col-4">
                <Form>
                  <Form.Group className="entitlements-userid-save-btn-body">
                    <button
                      type="button"
                      onClick={handleUserIdSave}
                      className="btn btn-dark float-end btn-opacity"
                    >
                      Save
                    </button>
                  </Form.Group>
                </Form>
              </div>
            </Row>
          </div>
        </ModalBox>
      </>
    );
  },
  (op, np) =>
    op?.filteredDistributorUserIdData === np?.filteredDistributorUserIdData,
);

export default DistributorUserIdModal;
